/**
 * viewTabsRules.js — LUẬT THUẦN cho các TAB của trang xem (DataView), tương đương `view_tabs` của
 * listinfo mà ViewBase.js vẽ.
 *
 * Một tab khai: `{ code, name, default_condition?, default_data?, content_data?, data_pickers? }`
 *   code = "detailform"      -> thông tin chung (bộ mới luôn vẽ phần này ở trên, nên tab này bị bỏ)
 *   code = "file" | "files"  -> tệp đính kèm của bản ghi
 *   code = "note"            -> ghi chú
 *   code = "chatroom"        -> thảo luận
 *   code = "comment"         -> ý kiến
 *   code = "task"            -> công việc gắn với bản ghi (mặc định lọc/gán `id_link`)
 *   code = "content"         -> HTML dựng từ template `content_data`
 *   mã khác                  -> DANH SÁCH của màn hình đó, lọc theo `default_condition`, thêm mới thì
 *                               điền sẵn `default_data`
 *
 * `default_condition` / `default_data` / `data_pickers` là BIỂU THỨC JS (không phải JSON), chạy với
 * `(master, moment, numeral)` — đúng `ViewBase.createTab`. `content_data` là TEMPLATE STRING.
 * Đã là object (cấu hình JSON, hoặc parseDataListConfig đã parse) thì dùng thẳng.
 *
 * KHÔNG import gì — thư viện tiêm qua `deps` ({ moment, numeral }).
 */

const SPECIAL_KINDS = ['file', 'files', 'note', 'chatroom', 'comment', 'task', 'content'];

/** Loại tab theo `code` (không phân biệt hoa thường). */
function viewTabKind(tab) {
	const c = String((tab && tab.code) || '').trim().toLowerCase();
	if (!c) return null;
	if (c === 'detailform') return 'detailform';
	if (SPECIAL_KINDS.indexOf(c) >= 0) return c;
	return 'list';
}

/** Chuẩn hoá danh sách tab: bỏ tab không có mã, bỏ `detailform` (đã vẽ sẵn ở phần trên), bỏ trùng. */
function normalizeViewTabs(viewTabs) {
	const out = [];
	const seen = new Set();
	for (const t of (Array.isArray(viewTabs) ? viewTabs : [])) {
		const kind = viewTabKind(t);
		if (!kind || kind === 'detailform') continue;
		if (t.visible === false || t.status === false) continue;
		const key = String(t.code).trim().toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		out.push({ ...t, kind, key: 'view_' + key, title: t.name || t.label || t.code });
	}
	return out;
}

/**
 * Chạy một biểu thức của tab (`default_condition`/`default_data`/`data_pickers`).
 * Lỗi thì trả `undefined` và báo console — một tab hỏng không được làm trắng cả trang xem.
 */
function evalViewTabExpr(expr, master, deps) {
	if (expr === undefined || expr === null || expr === '') return undefined;
	if (typeof expr === 'object') return expr;
	if (typeof expr === 'function') {
		try { return expr(master, deps && deps.moment, deps && deps.numeral); } catch (e) { return undefined; }
	}
	try {
		const body = String(expr).indexOf('return ') >= 0 ? String(expr) : 'return ' + expr;
		return new Function('master', 'moment', 'numeral', body)(master || {}, deps && deps.moment, deps && deps.numeral);
	} catch (e) {
		console.error('[viewTabsRules] biểu thức tab lỗi:', (e && e.message) || e, expr);
		return undefined;
	}
}

/** `content_data` là TEMPLATE STRING: `Khách: ${master.ten_kh}`. */
function evalViewTabContent(tpl, master, deps) {
	if (!tpl) return '';
	try {
		return new Function('master', 'moment', 'numeral', 'return `' + String(tpl) + '`')(master || {}, deps && deps.moment, deps && deps.numeral);
	} catch (e) {
		console.error('[viewTabsRules] content_data lỗi:', (e && e.message) || e);
		return '';
	}
}

/**
 * Điều kiện + dữ liệu mặc định của một tab DANH SÁCH / công việc.
 * Tab `task` không khai gì thì lọc và gán theo `id_link` = _id bản ghi (đúng ViewBase).
 */
function viewTabListProps(tab, master, deps) {
	const cond = evalViewTabExpr(tab.default_condition, master, deps);
	const data = evalViewTabExpr(tab.default_data, master, deps);
	const id = master && (master._id || master.id);
	const condition = (cond && typeof cond === 'object') ? cond : (tab.kind === 'task' && id ? { id_link: id } : {});
	let defaultValues = (data && typeof data === 'object') ? data : (tab.kind === 'task' && id ? { id_link: id } : {});
	if (tab.kind === 'task' && master) {
		defaultValues = { ...defaultValues, phu_trach: master.phu_trach, attends: master.attends || [], group_id_link: id };
	}
	return { condition, defaultValues };
}

/**
 * Giá trị của một field có "rỗng" không — trang xem ẩn field rỗng.
 * Object rỗng / mảng rỗng / object mà mọi giá trị đều rỗng cũng là rỗng.
 */
function isEmptyViewValue(v) {
	if (v === undefined || v === null || v === '') return true;
	if (Array.isArray(v)) return v.length === 0;
	if (v instanceof Date) return false;
	if (typeof v === 'object') return Object.keys(v).every(k => isEmptyViewValue(v[k]));
	return false;
}

/**
 * Cách hiển thị một GIÁ TRỊ trên trang xem — chọn theo DỮ LIỆU THẬT, không chỉ theo `type` khai báo:
 * cấu hình cũ hay khai `String`/`Mixed` cho field thực chất chứa object hoặc mảng object, và
 * `String(value)` in ra "[object Object]".
 *   'detail'  mảng object -> bảng (có subFields thì theo cột khai báo, không thì tự suy cột)
 *   'object'  object      -> danh sách cặp nhãn/giá trị
 *   'chips'   mảng giá trị đơn -> các chip
 *   'scalar'  còn lại
 */
function viewValueShape(value, field) {
	const t = field && field.type;
	if (t === 'File' || t === 'FileArray' || t === 'HTML' || t === 'RichText') return 'scalar';
	if (Array.isArray(value)) {
		if (value.some(x => x && typeof x === 'object' && !(x instanceof Date))) return 'detail';
		if (t === 'Select' || t === 'Autocomplete') return 'scalar';   // nhãn đã có cách dịch riêng
		return 'chips';
	}
	if (value && typeof value === 'object' && !(value instanceof Date)) return 'object';
	return 'scalar';
}

/** Suy cột cho mảng object không khai `subFields`: hợp các khoá, bỏ khoá kỹ thuật, tối đa `max` cột. */
function inferColumns(rows, max) {
	const skip = ['_id', '__v', 'id_app', 'line', 'is_full_data'];
	const keys = [];
	for (const r of (rows || [])) {
		if (!r || typeof r !== 'object') continue;
		for (const k of Object.keys(r)) {
			if (skip.indexOf(k) >= 0 || keys.indexOf(k) >= 0) continue;
			keys.push(k);
		}
	}
	return keys.slice(0, max || 12).map(k => ({ field: k, label: k }));
}

export {
	SPECIAL_KINDS, viewTabKind, normalizeViewTabs, evalViewTabExpr, evalViewTabContent, viewTabListProps,
	isEmptyViewValue, viewValueShape, inferColumns,
};
